import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Index from "./pages/Index";

// Retry a code-split import once, then hard-reload: after a new deploy the old
// chunk filenames no longer exist and the import throws.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyRetry = <T extends { default: React.ComponentType<any> }>(factory: () => Promise<T>) =>
  lazy(() =>
    factory().catch(async () => {
      try {
        return await factory();
      } catch (err) {
        if (typeof window !== "undefined" && !sessionStorage.getItem("chunk-reloaded")) {
          sessionStorage.setItem("chunk-reloaded", "1");
          window.location.reload();
        }
        throw err;
      }
    })
  );

// Everything below the landing page is code-split so the initial bundle stays small.
const Services = lazyRetry(() => import("./pages/Services"));
const About = lazyRetry(() => import("./pages/About"));
const Contact = lazyRetry(() => import("./pages/Contact"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const AdminLogin = lazyRetry(() => import("./pages/AdminLogin"));
const AdminLeads = lazyRetry(() => import("./pages/AdminLeads"));
const AdminResetPassword = lazyRetry(() => import("./pages/AdminResetPassword"));
const AdminSeo = lazyRetry(() => import("./pages/AdminSeo"));
const Blog = lazyRetry(() => import("./pages/Blog"));
const BlogPost = lazyRetry(() => import("./pages/BlogPost"));

const PDFunnel = lazyRetry(() => import("./pages/PDFunnel"));
const AIPoliceFunnel = lazyRetry(() => import("./pages/AiPoliceFunnel"));
const ReceptionistFunnel = lazyRetry(() => import("./pages/ReceptionistFunnel"));
const HealthcareFunnel = lazyRetry(() => import("./pages/HealthcareFunnel"));
const WorkforceFunnel = lazyRetry(() => import("./pages/WorkforceFunnel"));
const EngamementFunnel = lazyRetry(() => import("./pages/EngamementFunnel"));
const RoboticsFunnel = lazyRetry(() => import("./pages/RoboticsFunnel"));
const ChatWidget = lazyRetry(() => import("@/components/ChatWidget"));
import { useVisitorTracking } from "@/hooks/use-visitor-tracking";

const queryClient = new QueryClient();

// Stale deploys leave old chunk URLs in the loaded page; reload once to recover.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (e) => {
    e.preventDefault();
    if (!sessionStorage.getItem("chunk-reloaded")) {
      sessionStorage.setItem("chunk-reloaded", "1");
      window.location.reload();
    }
  });
}


// Funnel pages render without shared nav/footer
const FUNNEL_ROUTES = ['/pd', '/aipolice', '/vaibot', '/receptionist', '/healthcare', '/workforce', '/engamement', '/robotics'];

const ADMIN_ROUTES = ['/admin'];

const AppLayout = () => {
  const location = useLocation();
  useVisitorTracking();
  const isFunnel = FUNNEL_ROUTES.includes(location.pathname.toLowerCase().replace(/\/+$/, '') || '/');
  const isAdmin = ADMIN_ROUTES.some((r) => location.pathname.startsWith(r));

  if (isFunnel) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/pd" element={<PDFunnel />} />
          <Route path="/aipolice" element={<AIPoliceFunnel />} />
          <Route path="/vaibot" element={<Navigate to="/aipolice" replace />} />
          <Route path="/receptionist" element={<ReceptionistFunnel />} />
          <Route path="/healthcare" element={<HealthcareFunnel />} />
          <Route path="/workforce" element={<WorkforceFunnel />} />
            <Route path="/enGAMEment" element={<EngamementFunnel />} />
            <Route path="/robotics" element={<RoboticsFunnel />} />


        </Routes>
        <ChatWidget />
      </Suspense>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Navigation />
      <main className="flex-1">
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin/seo" element={<AdminSeo />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <Suspense fallback={null}>{!isAdmin && <ChatWidget />}</Suspense>
    </div>
  );
};


const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
